import { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { FiMail, FiPhone, FiMapPin, FiCheckCircle, FiXCircle, FiRefreshCw } from 'react-icons/fi';
import { getDemoRequests, reviewDemoRequest } from '../../services/api';
import styles from './DemoRequestsPage.module.css';

const TABS = [
  { id: 'pending',  label: 'Pending' },
  { id: 'approved', label: 'Approved' },
  { id: 'rejected', label: 'Rejected' },
  { id: 'all',      label: 'All' },
];

const fmt = (d) => d
  ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
  : '—';

const fmtDate = (d) => d
  ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })
  : '—';

const formatAddress = (r) =>
  [r.addressLine1, r.addressLine2, r.city, r.state, r.pincode].filter(Boolean).join(', ');

const DemoRequestsPage = () => {
  const [tab, setTab]                     = useState('pending');
  const [requests, setRequests]           = useState([]);
  const [loading, setLoading]             = useState(true);
  const [detail, setDetail]               = useState(null);
  const [note, setNote]                   = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getDemoRequests(tab);
      setRequests(res.data.data || []);
    } catch {
      toast.error('Failed to load demo requests');
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  const openDetail = (request) => {
    setDetail(request);
    setNote('');
  };

  const handleAction = async (action) => {
    if (!detail) return;
    if (action === 'approved' && !note.trim()) {
      toast.error('Please enter access instructions / credentials in the note before approving');
      return;
    }
    setActionLoading(true);
    try {
      await reviewDemoRequest(detail.id, { action, adminNote: note });
      toast.success(action === 'approved' ? 'Demo request approved — approval email sent' : 'Demo request rejected');
      setDetail(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.message || `Failed to ${action} request`);
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h2>Demo Requests</h2>
          <p style={{ color: 'var(--color-text-muted)', fontSize: '0.9rem', marginTop: 4 }}>
            Review and approve 14-day trial access requests
          </p>
        </div>
        <button className="btn btn-ghost btn-sm" onClick={load} title="Refresh">
          <FiRefreshCw size={14} /> Refresh
        </button>
      </div>

      <div className={styles.tabNav}>
        {TABS.map((t) => (
          <button
            key={t.id}
            className={`${styles.tabBtn} ${tab === t.id ? styles.activeTab : ''}`}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="loading"><div className="spinner" /></div>
      ) : requests.length === 0 ? (
        <div className="empty-state">
          <p>No {tab === 'all' ? '' : tab} demo requests found.</p>
        </div>
      ) : (
        <div className="card">
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Name &amp; Derasar</th>
                  <th>Contact</th>
                  <th>Address</th>
                  <th>Status</th>
                  <th>Submitted</th>
                  <th className={styles.actionsCol}></th>
                </tr>
              </thead>
              <tbody>
                {requests.map((r) => (
                  <tr key={r.id} className={styles.rowClickable} onClick={() => openDetail(r)}>
                    <td>
                      <div className={styles.cellName}>{r.name}</div>
                      <div className={styles.cellSub}>{r.derasarName}</div>
                    </td>
                    <td>
                      <div className={styles.cellSub}><FiMail size={11} /> {r.email}</div>
                      {r.phone && <div className={styles.cellSub}><FiPhone size={11} /> {r.phone}</div>}
                    </td>
                    <td>
                      {formatAddress(r) ? (
                        <div className={styles.cellSub}><FiMapPin size={11} /> {formatAddress(r)}</div>
                      ) : '—'}
                    </td>
                    <td><span className={`badge badge-${r.status}`}>{r.status}</span></td>
                    <td style={{ whiteSpace: 'nowrap' }}>{fmt(r.createdAt)}</td>
                    <td className={styles.actionsCol} onClick={(e) => e.stopPropagation()}>
                      <button className="btn btn-secondary btn-sm" onClick={() => openDetail(r)}>View</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {detail && (
        <div className="modal-overlay" onClick={() => setDetail(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>{detail.name} — {detail.derasarName}</h3>

            <div className={styles.detailMeta}>
              <div className={styles.cellSub}><FiMail size={12} /> {detail.email}</div>
              {detail.phone && <div className={styles.cellSub}><FiPhone size={12} /> {detail.phone}</div>}
              {formatAddress(detail) && <div className={styles.cellSub}><FiMapPin size={12} /> {formatAddress(detail)}</div>}
            </div>

            {detail.message && (
              <div className={styles.cardMessage}>{detail.message}</div>
            )}

            {detail.status === 'approved' && detail.accessExpiresAt && (
              <div className={styles.expiry}>
                Trial access expires: <span className={styles.expiryValue}>{fmtDate(detail.accessExpiresAt)}</span>
              </div>
            )}

            {detail.adminNote && detail.status !== 'pending' && (
              <div style={{ marginBottom: 16 }}>
                <div className={styles.adminNoteLabel}>Admin Note</div>
                <div className={styles.adminNote}>{detail.adminNote}</div>
              </div>
            )}

            {detail.status === 'pending' && (
              <div className="form-group" style={{ marginTop: 16 }}>
                <label>Access Instructions</label>
                <textarea
                  rows={3}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Enter access instructions (URL, login credentials, etc.) — this note is emailed to the requester on approval. Required for approval."
                />
              </div>
            )}

            <div className="modal-actions">
              <button className="btn btn-ghost" onClick={() => setDetail(null)}>Close</button>
              {detail.status === 'pending' && (
                <>
                  <button className="btn btn-danger" disabled={actionLoading} onClick={() => handleAction('rejected')}>
                    <FiXCircle size={14} /> Reject
                  </button>
                  <button className="btn btn-success" disabled={actionLoading} onClick={() => handleAction('approved')}>
                    <FiCheckCircle size={14} /> Approve &amp; Send Access
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DemoRequestsPage;
